export function queryStringToObject(query: string): any {
  const pairs = query.slice(1).split("&");
  const result = {};
  pairs.forEach(pair => {
    const p = pair.split("=");
    if (p[0] !== "") {
      result[p[0]] = decodeURIComponent(p[1] || "");
    }
  });
  return JSON.parse(JSON.stringify(result));
}

let unqId = 0;
export function uniqId(prefix = "id") {
  // eslint-disable-next-line no-plusplus
  unqId++;
  return `${prefix}${unqId}`;
}

/**
 * Run a function if it is safe to do so when taking SSR into account.
 * @param func The function to run if it is safe to do so.
 * @param dependencies Any extra dependencies to check.
 */
export function ssrSafe<T>(
  func: () => T,
  dependencies: any[] = [
    (typeof process === "object" && process.browser) ||
      typeof window === "object"
  ]
) {
  // ensures browser specific functions are run only if browser is available whether in ssr or client
  if (dependencies.every(Boolean)) {
    return func();
  }

  return undefined;
}

export function makeSsrSafe<T extends CallableFunction>(
  func: T,
  dependencies?: any[]
): T {
  return function (this: ThisParameterType<T>, ...args: any[]) {
    return ssrSafe(() => (func as any).call(this, ...args), dependencies);
  } as any as T;
}

export const isIE = makeSsrSafe(() => Boolean(/MSIE|Trident/i.test(window.navigator.userAgent)));

/**
 * Debounce the passed function. This means that the function will only fire once while
 * it is being called within the threshold.
 * @param fn The function to be debounced
 * @param threshold How long to wait before allowing the function to be triggered again. Default is `100`.
 * @param immediate Whether to trigger the function at the start instead of after the threshold. Default is `false`.
 * @return A version of this function that will only trigger when it isn't called for the threshold amount of time.
 */
// tslint:disable-next-line: no-any
export const debounce = makeSsrSafe(
  <T extends (...args: any[]) => void>(
    fn: T,
    threshold = 100,
    immediate = false
  ) => {
    let timeout: number | undefined;

    return function (this: T, ...args: unknown[]) {
      const call = () => {
        timeout = undefined;

        if (!immediate) {
          return fn.apply(this, args);
        }
      };

      if (immediate && !timeout) {
        call();
      }

      clearTimeout(timeout!);
      timeout = window.setTimeout(call, threshold);
    } as T;
  }
);

/**
 * @param {string} parentTestId testId of parent component
 * @param {string} component Callee component's definition. Ex: Button
 * @param {string} testIdSuffix Describes callee component's feature/property. Ex: SelectPrice
 */
export function getTestId(
  parentTestId: string | undefined,
  component: string,
  testIdSuffix?: string
) {
  return parentTestId ?
    `${parentTestId}_${component}${testIdSuffix ? `_${testIdSuffix}` : ``}` :
    ``;
}
export function getNthNumber(num: number) {
  const lastDigit = num % 10,
    lastTwoDigit = num % 100;
  if (lastDigit === 1 && lastTwoDigit !== 11) {
    return `${num  }st`;
  }
  if (lastDigit === 2 && lastTwoDigit !== 12) {
    return `${num  }nd`;
  }
  if (lastDigit === 3 && lastTwoDigit !== 13) {
    return `${num  }rd`;
  }
  return `${num  }th`;
}

export function updateClassName(
  id,
  hideInDesktop,
  hideInTablet,
  hideInMobile,
  classes
) {
  const classElement = document.getElementById(id);
  if (classElement) {
    let classText = "";
    classText += hideInDesktop ? classes.hideInDesktop : "";
    classText += hideInTablet ? classes.hideInTablet : "";
    classText += hideInMobile ? classes.hideInMobile : "";
    if (classText !== "") {
      classElement.classList.add(classText);
    }
  }
}

export const suppressComponent = (component, options) => {
  if (
    // eslint-disable-next-line no-prototype-builtins
    !component.hasOwnProperty("isAuthenticatedUser") &&
    // eslint-disable-next-line no-prototype-builtins
    !component.hasOwnProperty("recognizedUserData")
  ) {
    return false;
  } else if (
    component.isAuthenticatedUser === "visible" &&
    component.recognizedUserData === "visible"
  ) {
    return false;
  } else if (
    component.isAuthenticatedUser === "hidden" &&
    component.recognizedUserData === "hidden"
  ) {
    if (options.isAuthenticatedUser || options.recognizedUserData) {
      return true;
    }
    return false;
  } else if (
    component.isAuthenticatedUser === "visible" &&
    component.recognizedUserData === "hidden"
  ) {
    if (typeof options.recognizedUserData === "undefined") {
      return false;
    }
    if (
      typeof options.recognizedUserData !== "undefined" &&
      options.recognizedUserData
    ) {
      return true;
    }
    return false;
  } else if (
    component.isAuthenticatedUser === "hidden" &&
    component.recognizedUserData === "visible"
  ) {
    if (
      typeof options.isAuthenticatedUser !== "undefined" &&
      options.isAuthenticatedUser
    ) {
      return true;
    }
    return false;
  }
  return false;
};

export const pluckArray = (arrList, key) => {
  // eslint-disable-next-line curly
  if(!arrList.length) return []
  return arrList.map(c => c[key])
}
export const chnlPkgReference = () => [
  {
    name: 'ATT_TV_ENTERTAINMENT',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_CHOICE',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_ULTIMATE',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_PREMIER',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_XTRA',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_OPTIMO_MAS',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_ENTERTAINMENT',
    categoryId: 'ENGLISH'
  },
  {
    name: 'DTV_CHOICE',
    categoryId: 'ENGLISH'
  },
  {
    name: 'DTV_ULTIMATE',
    categoryId: 'ENGLISH'
  },
  {
    name: 'DTV_PREMIER',
    categoryId: 'ENGLISH'
  },
  {
    name: 'DTV_OPTIMO_MAS',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_MAS_LATINO',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_MAS_ULTRA',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_LO_MAXIMO',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_BASIC_CHOICE',
    categoryId: 'WORLD_DIRECT'
  },
  {
    name: 'DTV_PREFERRED_CHOICE_ALL_IN',
    categoryId: 'WORLD_DIRECT'
  },
  {
    name: 'DTV_PREFERRED_CHOICE',
    categoryId: 'WORLD_DIRECT'
  },
  {
    name: 'MYFREE_DTV',
    categoryId: 'ENGLISH'
  }
]

// WAT-12195: shop saves every streaming and genre cart under `build` (cartType 'streaming' or
// 'genre' for UCCP, 'streaming' or 'build' for MCCP, which also sets planType 'mccp'). Satellite
// still saves under `satellite`. No other key is read.
const readCart = (key: string): any => {
  try {
    return JSON.parse(window?.localStorage?.getItem?.(key) || 'null');
  } catch (err) {
    console.error(`[getSavedCart] could not read saved cart "${key}"`, err);
    return null;
  }
};

// Opening shop writes an empty `build` cart straight away, so an empty cart never wins.
// A genre cart carries the container offer in `package` before any pack is picked, so it is
// judged by its base packs; a streaming cart by its package.
const isEmptyCart = (cart: any): boolean => {
  if (cart?.cartType === 'satellite') {
    return isEmptyObj(cart?.package) && !(cart?.addOns?.length > 0) && !(cart?.equipment?.devices?.length > 0);
  }
  if (['genre', 'build'].includes(cart?.cartType)) {
    return !(cart?.genreBasePackages?.length > 0);
  }
  return !cart?.cartType || isEmptyObj(cart?.package);
};

// Epoch ms of the cart's last package change; sales-ui and UXE both stamp it. Missing or unparseable → 0.
const cartTime = (cart: any): number => new Date(cart?.packageDetail?.lastUpdated ?? 0)?.getTime() || 0;

/**
 * The shopper's cart. A shopper can hold a build cart and a satellite cart at once, so when both
 * have content the one changed last wins. An undated cart counts as oldest, and a tie goes to `build`.
 * Mirrors `getSavedCart` in video-lab's `utils/UtilityFunctions.ts` (DTV-IT/apm0001659-dpd-video-lab#2902).
 */
export function getSavedCart(): any | null {
  // Touching `window.localStorage` itself throws when storage is blocked, so readCart's try owns that check.
  if (typeof window !== 'object') {
    return null;
  }
  const [build, satellite] = [readCart('build'), readCart('satellite')].map(cart => (isEmptyCart(cart) ? null : cart));
  if (!build || !satellite) {
    return build ?? satellite ?? null;
  }
  return cartTime(satellite) > cartTime(build) ? satellite : build;
}

export const isEmptyObj = (obj: any): boolean => !obj || typeof obj !== 'object' || Object.keys(obj).length === 0;

// CMS-sourced target URLs (e.g. CTA `targeturl`) flow to `<a href>` and `window.open`. A non-http(s)
// scheme (javascript:, data:, vbscript:) executes in-origin, so validate the scheme before use and
// drop anything that isn't http/https. Relative URLs are preserved (they resolve to the site origin).
// A fixed https base parses/validates the scheme, so this is SSR-safe (no dependency on window).
export const safeHttpUrl = (targetUrl?: string): string | undefined => {
  if (!targetUrl) {
    return undefined;
  }
  try {
    const { protocol } = new URL(targetUrl, 'https://www.directv.com');
    return protocol === 'http:' || protocol === 'https:' ? targetUrl : undefined;
  } catch {
    return undefined;
  }
};

export const findDuplicateIds = () => {
  const allElementIds = document.querySelectorAll<HTMLElement>('[id]');

  const idSet = new Set<string>();
  const duplicateIds: Record<string, number> = {};
  const malformedIds: string[] = [];

  allElementIds.forEach(element => {
    // eslint-disable-next-line prefer-destructuring
    const id = element.id;
    // eslint-disable-next-line curly
    if(id === '') return; // Skip empty IDs
    if(idSet.has(id)) {
      if(id in duplicateIds) {
        duplicateIds[id] = duplicateIds[id] + 1;
      } else {
        duplicateIds[id] = 2;
      }
    } else {
      idSet.add(id);
    }
    if(id.includes(' ') || id.includes('undefined')) {
      malformedIds.push(id);
    }
  });

  if(Object.keys(duplicateIds).length > 0 || malformedIds.length > 0) {
    console.warn('Duplicate/ incorrectly formatted IDs found', "\nDuplicates (id, occurrences): ", duplicateIds, "\nIncorrectly Formatted (id):", malformedIds);
  } else {
    console.warn('No duplicate/ incorrectly formatted IDs found');
  }

  console.warn("\nAll Existing Unique IDs (Please do not use in new components): ", [...idSet]);
}
